import type { Components, Theme } from '@mui/material/styles';

/**
 * Returns MUI component style overrides scoped to the given colour mode.
 *
 * Keeping this as a function (rather than a static object) lets us vary
 * tokens such as the Drawer background between light and dark themes.
 */
export function getComponentOverrides(
  mode: 'light' | 'dark',
): Components<Omit<Theme, 'components'>> {
  const isLight = mode === 'light';

  return {
    // ---- Buttons --------------------------------------------------------
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none' as const,
          fontWeight: 600,
          padding: '8px 20px',
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '1rem',
        },
      },
    },

    // ---- Surfaces -------------------------------------------------------
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
        },
      },
    },

    // ---- AppBar ---------------------------------------------------------
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'transparent',
      },
      styleOverrides: {
        root: {
          backgroundColor: isLight
            ? 'rgba(245, 245, 240, 0.8)'
            : 'rgba(26, 30, 29, 0.8)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
        },
      },
    },

    // ---- Chip -----------------------------------------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },

    // ---- Text fields ----------------------------------------------------
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isLight
              ? 'rgba(0,0,0,0.15)'
              : 'rgba(255,255,255,0.15)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isLight
              ? 'rgba(0,0,0,0.3)'
              : 'rgba(255,255,255,0.3)',
          },
        },
      },
    },

    // ---- Table ----------------------------------------------------------
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: isLight
            ? 'rgba(0,0,0,0.02)'
            : 'rgba(255,255,255,0.03)',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },

    // ---- Drawer (sidebar) -----------------------------------------------
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: isLight ? '#3D4542' : '#1A1E1D',
          color: '#F5F5F0',
          borderRight: 'none',
        },
      },
    },

    // ---- Tooltip --------------------------------------------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },

    // ---- Dialog ---------------------------------------------------------
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },

    // ---- CssBaseline (global styles) ------------------------------------
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin' as const,
          scrollbarColor: `${isLight ? '#BDBDBD' : '#424242'} transparent`,
        },
      },
    },
  };
}
